import { Component, OnInit, HostListener } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from '../../services/auth/authentication.service';
import { ApiService } from '../../services/api/api.service';

// Android and iOS each get their own always-visible section instead of a
// platform dropdown that hid one of them — both are common things to check
// at once (e.g. confirming both are NOT stuck in maintenance mode).
const PLATFORMS = ['ANDROID', 'IOS'];

@Component({
  selector: 'app-konfigurasi',
  templateUrl: './konfigurasi.component.html',
  styleUrls: ['./konfigurasi.component.css']
})
export class KonfigurasiComponent implements OnInit {

  isForce : any = [{'id':1,'text':'Ya'},{'id':0,'text':'Tidak'}];
  isMaintenance : any = [{'id':1,'text':'Ya'},{'id':0,'text':'Tidak'}];

  // one entry per platform: {platform, form, isLoading}
  sections : any[] = [];

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private authService: AuthenticationService,
    private apiService: ApiService,
  ) { }

  ngOnInit(): void {
    this.sections = PLATFORMS.map(platform => ({
      platform,
      form: this.buildForm(platform),
      isLoading: true,
    }));
    this.sections.forEach(s => this.getData(s));
    this.loadSettings();
  }

  // ===== Fitur: app feature switches (app_setting) =====
  // voucher_ios / voucher_android: show voucher redemption in the app on that
  // platform. Off by default — App Store 3.1.1 and Google Play billing allow
  // only their own purchases to unlock content. Saved on toggle.
  settings : any = null; // {voucher_ios: bool, voucher_android: bool}
  settingSaving : string = '';

  voucherKey(platform : string){ return platform == 'ANDROID' ? 'voucher_android' : 'voucher_ios'; }

  loadSettings(){
    this.apiService.methodGetSilent(`configuration/settings`, this.authService.getToken()).subscribe((response : any) => {
      this.settings = (response && response.status == 200 && response.data) || {voucher_ios: false, voucher_android: false};
    }, () => { this.settings = {voucher_ios: false, voucher_android: false}; });
  }

  toggleSetting(key : string, label : string){
    if(!this.settings || this.settingSaving){ return; }
    const value = !this.settings[key];
    this.settingSaving = key;
    this.apiService.methodPutSilent(`configuration/settings`, {key, value}, this.authService.getToken()).subscribe(async (response : any) => {
      this.settingSaving = '';
      if(response && response.status == 200){
        this.settings[key] = value;
        await this.toastr.success(`${label} ${value ? 'ditampilkan' : 'disembunyikan'}. Berlaku saat aplikasi dibuka ulang.`);
      }else{
        await this.toastr.error((response && response.message) || 'Coba lagi.', 'Gagal menyimpan');
      }
    }, async () => {
      this.settingSaving = '';
      await this.toastr.error('Periksa koneksi lalu coba lagi.', 'Gagal menyimpan');
    });
  }

  private buildForm(platform : string){
    return this.fb.group({
      id : new FormControl(''),
      admin_id : new FormControl(this.authService.getSession().id, [Validators.required]),
      platform : new FormControl(platform, [Validators.required]),
      version_code : new FormControl('', [Validators.required]),
      version_name : new FormControl('', [Validators.required]),
      is_force_update : new FormControl('', [Validators.required]),
      is_maintenance : new FormControl('', [Validators.required])
    });
  }

  async getData(section : any){
    section.isLoading = true;
    try{
      this.apiService.methodGetSilent(`configuration?platform=${section.platform}`, this.authService.getToken()).subscribe((response) => {
        section.isLoading = false;
        if(response && response.status == 200 && response.total && response.data.length){
          section.form.patchValue(Object.assign({}, response.data[0]));
        }
        // else: no config saved yet for this platform — leave the form blank
      }, () => { section.isLoading = false; });
    }catch(error){
      section.isLoading = false;
      console.log(error);
    }
  }

  isInvalid(section : any, control : string){
    const c = section.form.get(control);
    return c && c.invalid && (c.touched || c.dirty);
  }

  onSubmit(section : any){
    if(section.form.invalid){
      section.form.markAllAsTouched();
      return;
    }
    this.openConfirm(section);
  }

  // ===== save confirmation (custom dialog, matches the rest of the app) =====
  isConfirm : any = null; // the section pending confirmation
  isSaving : boolean = false;

  private openConfirm(section : any){
    this.isConfirm = section;
    document.body.style.overflow = 'hidden'; // lock the page scroll behind the dialog
    setTimeout(() => { // focus the Cancel button so the keyboard lands inside the dialog
      const btn: any = document.querySelector('.hds-dialog .vl-btn-ghost');
      if(btn){ btn.focus(); }
    });
  }

  closeConfirm(){
    if(this.isSaving){ return; } // never close while a save is in flight
    this.isConfirm = null;
    document.body.style.overflow = '';
  }

  @HostListener('document:keydown.escape')
  onEscapeConfirm(){
    if(this.isConfirm){ this.closeConfirm(); }
  }

  confirmSave(){
    const section = this.isConfirm;
    if(!section || this.isSaving){ return; }
    this.isSaving = true;
    this.apiService.methodPostSilent(`configuration`, section.form.value, this.authService.getToken()).subscribe(async (response) => {
      this.isSaving = false;
      if(response && response.status == 200){
        this.closeConfirm();
        await this.toastr.success(`Konfigurasi ${section.platform} disimpan`);
        await this.getData(section);
      }else{
        // failure: keep the dialog open so the admin can retry
        await this.toastr.error((response && response.message) || `Periksa isian lalu coba lagi.`, `Gagal menyimpan`);
      }
    }, async () => {
      this.isSaving = false;
      await this.toastr.error(`Periksa koneksi lalu coba lagi.`, `Gagal menyimpan`);
    });
  }

}
